import React, { FC, useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Route, Navigate, Routes, useNavigate, useLocation, useParams } from "react-router";
import { BackArrow, Button, Tabs, TopProgress } from "../../../../components";
import ModalVariables from "../../../../components/modal-alerts";
import { useUi } from "../../../../context/ui.context";
import { kubernetesService } from "../../../../api";
import { StaffClusterDetail } from "../types";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import { usePageTitle } from "../../../../context/navigationContext";
import { handleStatusName } from "../../../../utils/handle-status-name";
import { statusColor } from "../../../../utils/statusColor";
import {
  Container,
  HeadWrapper,
  InstanceName,
  NavWrapper,
} from "./styles";
import Overview from "./components/overview";
import Addons from "./components/addons";
import styled from "styled-components";

const TRANSITIONAL_STATUSES = ['creating', 'deleting', 'updating', 'upgrading'];

const StateWrapper = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const GreenDot = styled.div<{ active?: string }>`
  max-width: 12px;
  max-height: 12px;
  min-width: 12px;
  min-height: 12px;
  background-color: ${({ active }) => statusColor(active)};
  border-radius: 100px;
`;

const Greentext = styled.span<{ active?: string }>`
  color: ${({ active }) => statusColor(active)};
  text-transform: capitalize;
`;

const ClusterPage: FC = () => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const { clusterId } = useParams();
  const location = useLocation();

  const [cluster, setCluster] = useState<StaffClusterDetail | null>(null);
  usePageTitle(cluster?.name ? `${t("CLUSTER")}: ${cluster.name}` : t("CLUSTER"));
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>("overview");

  const [viewModal, setViewModal] = useState(false);
  const [activeAnimation, setActiveAnimation] = useState(false);
  const [modalType, setModalType] = useState<any>('');
  const [alertTitle, setAlertTitle] = useState('');
  const [alertSubTitle, setAlertSubTitle] = useState<any>('');

  const [tabs] = useState([
    { title: t("OVERVIEW"), slug: "overview", id: "1" },
    { title: t("ADDONS_TAB"), slug: "addons", id: "2" },
  ]);

  useEffect(() => {
    const lastSegment = location.pathname.split("/").pop();
    tabs.forEach((tab) => {
      if (lastSegment === tab.slug) {
        setActiveTab(tab.slug);
      }
    });
  }, [location, tabs]);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadCluster = useCallback(() => {
    if (!clusterId) return;
    kubernetesService
      .getClusterById(clusterId)
      .then((res) => {
        setCluster(res.data);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_CLUSTER");
        viewAlert({ severity: "error", message });
        navigate("/openstack/clusters");
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [clusterId, t, viewAlert, navigate]);

  useEffect(() => {
    loadCluster();
    intervalRef.current = setInterval(() => {
      loadCluster();
    }, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [loadCluster]);

  const performDelete = useCallback(() => {
    if (!clusterId) return;
    kubernetesService
      .deleteCluster(clusterId)
      .then(() => {
        viewAlert({ severity: "information", message: t("CLUSTER_DELETED") });
        navigate("/openstack/clusters");
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_DELETE_CLUSTER");
        viewAlert({ severity: "error", message });
      });
  }, [clusterId, t, viewAlert, navigate]);

  const closeModal = () => {
    if (modalType === 'universal') {
      performDelete();
    }
    setViewModal(false);
    setActiveAnimation(false);
    setModalType('');
    setAlertSubTitle('');
  };

  const closeModalCancel = () => {
    setViewModal(false);
    setActiveAnimation(false);
    setModalType('');
    setAlertSubTitle('');
  };

  const handleDelete = useCallback(() => {
    if (!clusterId || !cluster) return;
    setAlertSubTitle(t("CONFIRM_DELETE_CLUSTER"));
    setModalType('universal');
    setAlertTitle(t("DELETE_CLUSTER"));
    setViewModal(true);
    setActiveAnimation(true);
  }, [clusterId, cluster, t]);

  const isTransitional = cluster?.status
    ? TRANSITIONAL_STATUSES.includes(cluster.status.toLowerCase())
    : false;

  if (pageLoading) return <TopProgress loading={pageLoading} />;

  return (
    <Container>
      <HeadWrapper>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <BackArrow fallbackPath="/openstack/clusters" />
          <InstanceName>{cluster?.name || ""}</InstanceName>
          {cluster?.status && (
            <StateWrapper>
              <GreenDot active={cluster.status} />
              <Greentext active={cluster.status}>
                {handleStatusName(cluster.status, t)}
              </Greentext>
            </StateWrapper>
          )}
        </div>
        <Button
          onClick={handleDelete}
          variant="icon"
          disabled={isTransitional}
          title={t("DELETE_CLUSTER")}
          color="danger"
        >
          {t("DELETE_CLUSTER")}
        </Button>
      </HeadWrapper>
      <NavWrapper>
        <div>
          <Tabs
            tabs={tabs}
            size="display"
            blockNavigate
            location={activeTab}
            onChange={(slug: string) => {
              navigate(`/openstack/clusters/${clusterId}/${slug}`);
            }}
          />
        </div>
      </NavWrapper>
      <Routes>
        <Route path="/" element={<Navigate replace to="overview" />} />
        <Route
          path="/overview"
          element={<Overview cluster={cluster} onUpdate={loadCluster} />}
        />
        <Route
          path="/addons"
          element={<Addons clusterId={clusterId || ""} />}
        />
      </Routes>
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
      />
    </Container>
  );
};

export default ClusterPage;
